"use strict";
/**
 * The player's position, outside React, and honest about seeks in flight.
 *
 * Every surface samples its player a few times a second: the Website polls the
 * IFrame API, Mobile receives pushes from the WebView. Four readings a second
 * is plenty to stay accurate and nowhere near enough to animate, so this
 * EXTRAPOLATES between samples — playback time is predictable — and treats
 * each reading as a correction. A bar drawn from it moves every frame.
 *
 * The other half of its job is the one that was missing. A seek does not land
 * instantly: the request crosses an iframe or a WebView bridge, and the player
 * then buffers. Samples taken in that window still report the OLD position,
 * and code that believes them draws the bar snapping backwards and — worse —
 * reads its own seek as the viewer dragging away, which is how a scrub used to
 * drop the skim run. So a seek WE issued publishes its destination at once and
 * suppresses readings that still disagree with it, until the player agrees or
 * the evidence says the seek never took.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPlaybackClock = createPlaybackClock;
exports.createTickingPlaybackClock = createTickingPlaybackClock;
/** Milliseconds; monotonic where the platform has one. */
const defaultNow = () => typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
function createPlaybackClock(options = {}) {
    const { now = defaultNow, seekToleranceSeconds = 0.75, seekTimeoutMs = 1500, onActivityChange, } = options;
    /** The last trusted reading, and when it arrived on OUR timeline. */
    let anchor = 0;
    let anchoredAt = now();
    let playing = false;
    let published = 0;
    /**
     * `from` is where we really left, which is exactly what a stale echo
     * reports. `fromAt` is when we left it; `requestedAt` is when the latest
     * seek request was made (for timeout only).
     */
    let pendingSeek = null;
    const listeners = new Set();
    let active = false;
    const read = () => {
        if (!playing)
            return anchor;
        return Math.max(0, anchor + (now() - anchoredAt) / 1000);
    };
    const publish = () => {
        const next = read();
        if (next === published)
            return;
        published = next;
        listeners.forEach((listener) => listener());
    };
    const syncActivity = () => {
        const next = playing && listeners.size > 0;
        if (next === active)
            return;
        active = next;
        onActivityChange?.(next);
    };
    const anchorTo = (seconds) => {
        anchor = Math.max(0, seconds);
        anchoredAt = now();
    };
    return {
        get: () => published,
        read,
        subscribe: (listener) => {
            listeners.add(listener);
            syncActivity();
            return () => {
                listeners.delete(listener);
                syncActivity();
            };
        },
        sample: (seconds) => {
            if (pendingSeek) {
                const nowMs = now();
                const elapsedSinceRequest = nowMs - pendingSeek.requestedAt;
                const landed = Math.abs(seconds - pendingSeek.target) <= seekToleranceSeconds;
                // A stale echo reports where we LEFT — give or take however far the
                // video played on while the request was in flight. Knowing `from`
                // makes this exact: a reading near neither end is the viewer having
                // moved the player themselves, and is believed at once instead of
                // waiting out the deadline.
                const elapsedSinceDeparture = nowMs - pendingSeek.fromAt;
                const echo = seconds >= pendingSeek.from - seekToleranceSeconds &&
                    seconds <=
                        pendingSeek.from +
                            seekToleranceSeconds +
                            elapsedSinceDeparture / 1000;
                if (!landed && echo && elapsedSinceRequest < seekTimeoutMs)
                    return;
                pendingSeek = null;
            }
            anchorTo(seconds);
            publish();
        },
        setPlaying: (next) => {
            if (next === playing)
                return;
            // Re-anchor on the transition, or the time spent paused is counted as
            // played the moment it resumes.
            anchorTo(read());
            playing = next;
            publish();
            syncActivity();
        },
        beginSeek: (seconds) => {
            // While a seek is still unconfirmed the player is, as far as anyone
            // knows, still where the FIRST one left — so a second scrub keeps that
            // departure point. Reading it back here would record the optimistic
            // destination instead, and the next stale echo would sit at neither end
            // and be believed, snapping the bar back to a position nobody chose.
            const requestAt = now();
            const target = Math.max(0, seconds);
            if (pendingSeek) {
                pendingSeek = {
                    from: pendingSeek.from,
                    fromAt: pendingSeek.fromAt,
                    target,
                    requestedAt: requestAt,
                };
            }
            else {
                pendingSeek = {
                    from: read(),
                    fromAt: requestAt,
                    target,
                    requestedAt: requestAt,
                };
            }
            anchorTo(seconds);
            publish();
        },
        isSettling: () => pendingSeek !== null,
        tick: publish,
        isActive: () => active,
        reset: (seconds = 0) => {
            pendingSeek = null;
            playing = false;
            anchorTo(seconds);
            publish();
            syncActivity();
        },
    };
}
/**
 * A clock that drives itself on animation frames.
 *
 * This is what turns four reports a second into a bar that moves at the
 * display's rate: between reports the position is extrapolated, and the frame
 * loop publishes it. The loop runs strictly while playback is advancing AND
 * something is subscribed, so an idle surface costs nothing.
 *
 * `requestAnimationFrame` is a host global in the browser and in React Native
 * alike; where there is none (SSR, a test runner) the clock still publishes on
 * every sample and seek, just without the frames in between.
 */
function createTickingPlaybackClock(options = {}) {
    const canAnimate = typeof requestAnimationFrame === 'function' &&
        typeof cancelAnimationFrame === 'function';
    let frame = null;
    const stop = () => {
        if (frame === null)
            return;
        cancelAnimationFrame(frame);
        frame = null;
    };
    const clock = createPlaybackClock({
        ...options,
        onActivityChange: (active) => {
            if (!active) {
                stop();
                return;
            }
            if (!canAnimate || frame !== null)
                return;
            const loop = () => {
                clock.tick();
                // Re-checked per frame as well as on the callback, so a loop can never
                // outlive what it draws.
                frame = clock.isActive() ? requestAnimationFrame(loop) : null;
            };
            frame = requestAnimationFrame(loop);
        },
    });
    const { reset } = clock;
    return {
        ...clock,
        reset: (seconds) => {
            stop();
            reset(seconds);
        },
    };
}
